import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  inject,
  Output,
  EventEmitter,
  ChangeDetectionStrategy
} from '@angular/core';
import { select, Selection } from 'd3-selection';
import { scaleBand, scaleLinear, ScaleBand, ScaleLinear } from 'd3-scale';
import { max } from 'd3-array';
import 'd3-transition';
import { ThemeService, Theme } from '../../../../../core/services/theme.service';
import { Subscription } from 'rxjs';

export interface PaymentRecord {
  arrangement_id?: string | number;
  patient_name?: string;
  contract_code?: string;
  remaining_payments?: number;
  recurring_amount?: number;
  [key: string]: any;
}

@Component({
  selector: 'app-vertical-single-bar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './vertical-single-bar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
  `]
})
export class VerticalSingleBarComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('chartContainer') chartContainer!: ElementRef;
  
  @Input() data: any[] = [];
  @Input() maxBars: number = 10;
  @Input() barHeight: number = 200;
  @Input() showRankBadges: boolean = true;
  @Input() showLabels: boolean = true;
  @Input() animationDuration: number = 500;

  // Generic Data Keys
  @Input() valueKey: string = 'remaining_payments';
  @Input() labelKey: string = 'patient_name';
  @Input() subLabelKey: string = 'contract_code';
  @Input() amountKey: string = 'recurring_amount';
  @Input() secondaryLabelText: string = '$ 202/ pay'; 
  @Input() showSecondaryLabel: boolean = false;
  @Input() showViewAllButton: boolean = true;
  @Input() valueFormat: 'number' | 'currency' | 'percentage' = 'number';

  @Input() amountSuffix: string = '/pay';

  @Input() valueSuffix: string = ' Left';

  @Output() viewAll = new EventEmitter<void>();

  private svg?: Selection<SVGSVGElement, unknown, null, undefined>;
  private resizeObserver?: ResizeObserver;
  private themeSubscription?: Subscription;
  private currentTheme: Theme = 'light';
  private themeService = inject(ThemeService);

  ngAfterViewInit() {
    this.currentTheme = this.themeService.getTheme();
    this.themeSubscription = this.themeService.theme$.subscribe((theme) => {
      this.currentTheme = theme;
      this.renderChart();
    });

    this.observeResize();
    this.renderChart();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] && !changes['data'].firstChange) {
      this.renderChart();
    }
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
    this.themeSubscription?.unsubscribe();
  }

  private observeResize() {
    this.resizeObserver = new ResizeObserver(() => this.renderChart());
    this.resizeObserver.observe(this.chartContainer?.nativeElement);
  }

  private clearChart() {
    select(this.chartContainer?.nativeElement).selectAll('*').remove();
  }

  private getThemeColors() {
    const isDark = this.currentTheme === 'dark';
    return {
      // Rank badge colors
      rankGold: ['#fbbf24', '#f97316'],
      rankSilver: isDark ? ['#64748b', '#475569'] : ['#cbd5e1', '#94a3b8'],
      rankBronze: ['#d97706', '#b45309'],
      rankDefault: isDark ? '#2e2e33' : '#f1f5f9',
      rankDefaultText: isDark ? '#a1a1aa' : '#64748b',
      
      // Bar gradients
      barGradient1: ['#9333ea', '#a855f7', '#c084fc'],
      barGradient2: ['#7e22ce', '#9333ea', '#a855f7'],
      barGradient3: ['#6b21a8', '#7e22ce', '#9333ea'],
      barGradientDefault: isDark ? ['#581c87', '#6b21a8', '#7e22ce'] : ['#6b21a8', '#7e22ce', '#9333ea'],
      
      // Background
      barBg: isDark ? '#1e1e21' : '#f1f5f9',
      blobBg: isDark ? '#2e2e33' : '#f8fafc',
      
      // Text colors
      textPrimary: isDark ? '#e4e4e7' : '#1e293b',
      textSecondary: isDark ? '#a1a1aa' : '#64748b',
      textAmount: isDark ? '#d4d4d8' : '#334155',
    };
  }


  private renderChart() {
    this.clearChart();

    const container = this.chartContainer?.nativeElement;
    if (!container || !this.data || this.data.length === 0) return;

    const width = container.getBoundingClientRect().width;
    const displayData = this.data.slice(0, this.maxBars);
    const colors = this.getThemeColors();

    // Calculate bar width and spacing - optimized for 20 bars with proper spacing
    const barWidth = 60; 
    const spacing = 40; 
    const totalWidth = (barWidth * displayData.length) + (spacing * (displayData.length + 1));
    const needsScroll = totalWidth > width;
    
    // Create scrollable wrapper with flex centering
    const wrapper = select(container)
      .append('div')
      .style('width', '100%')
      .style('display', 'flex')
      .style('justify-content', needsScroll ? 'flex-start' : 'center')
      .style('overflow-x', needsScroll ? 'auto' : 'visible')
      .style('overflow-y', 'visible')
      .style('padding-bottom', '10px');

    // Create SVG with exact content width
    const svg = wrapper
      .append('svg')
      .attr('width', totalWidth)
      .attr('height', this.barHeight + 120)
      .style('overflow', 'visible');

    this.svg = svg;

    // Center content within SVG if it fits, otherwise use a single spacing margin
    const offsetX = spacing;
    const maxValue = max(displayData, d => d[this.valueKey] || 0) || 1;
    const yScale = scaleLinear()
      .domain([0, maxValue])
      .range([0, this.barHeight]);

    // Create bars
    displayData.forEach((record, i) => {
      const x = offsetX + (i * (barWidth + spacing));
      const value = record[this.valueKey] || 0;
      const barHeightScaled = yScale(value);

      // Create group for each bar
      const barGroup = svg.append('g')
        .attr('class', 'bar-group')
        .attr('transform', `translate(${x}, 0)`);

      // Bar background
      barGroup.append('rect')
        .attr('x', 0)
        .attr('y', 30)
        .attr('width', barWidth)
        .attr('height', this.barHeight)
        .attr('rx', 8)
        .attr('fill', colors.barBg);

      // Bar fill with gradient
      const gradientId = `gradient-${i}`;
      this.createGradient(svg, gradientId, i, colors);

      barGroup.append('rect')
        .attr('x', 0)
        .attr('y', 30 + this.barHeight)
        .attr('width', barWidth)
        .attr('height', 0)
        .attr('rx', 8)
        .attr('fill', `url(#${gradientId})`)
        .transition()
        .duration(this.animationDuration)
        .attr('y', 30 + this.barHeight - barHeightScaled)
        .attr('height', barHeightScaled);

      // Value label on bar
      if (this.showLabels && value > 0) {
        let labelText = `${value}`;
        if (this.valueFormat === 'percentage') labelText += '%';
        else if (this.valueFormat === 'number') labelText += this.valueSuffix;
        else if (this.valueFormat === 'currency') labelText = this.formatCurrency(value);

        const isLabelAbove = barHeightScaled < 25;
        const labelY = isLabelAbove 
            ? 30 + this.barHeight - barHeightScaled - 8 
            : 30 + this.barHeight - barHeightScaled + 14;

        barGroup.append('text')
          .attr('x', barWidth / 2)
          .attr('y', labelY)
          .attr('text-anchor', 'middle')
          .attr('fill', isLabelAbove ? colors.textPrimary : 'white')
          .attr('font-size', '11px')
          .attr('font-weight', 'bold')
          .style('opacity', 0)
          .text(labelText)
          .transition()
          .delay(this.animationDuration / 2)
          .duration(this.animationDuration / 2)
          .style('opacity', 1);
      }

      // Footer section - Primary Label (Patient Name / Admin Name)
      barGroup.append('text')
        .attr('x', barWidth / 2)
        .attr('y', 30 + this.barHeight + 38)
        .attr('text-anchor', 'middle')
        .attr('fill', colors.textPrimary)
        .attr('font-size', '11px')
        .attr('font-weight', '500')
        .style('opacity', 0)
        .text(this.truncateText(record[this.labelKey] || 'N/A', barWidth))
        .transition()
        .delay(this.animationDuration)
        .duration(300)
        .style('opacity', 1);

      // Secondary Label (Contract Code / Role)
      if (this.subLabelKey) {
        barGroup.append('text')
            .attr('x', barWidth / 2)
            .attr('y', 30 + this.barHeight + 52)
            .attr('text-anchor', 'middle')
            .attr('fill', colors.textSecondary)
            .attr('font-size', '10px')
            .style('opacity', 0)
            .text(this.truncateText(record[this.subLabelKey] || '', barWidth))
            .transition()
            .delay(this.animationDuration)
            .duration(300)
            .style('opacity', 1);
      }

      // Decoration Blob (Amount)
      if (this.amountKey && record[this.amountKey] !== undefined) {
        const amountText = this.formatCurrency(record[this.amountKey]) + this.amountSuffix;
        const blobWidth = Math.max(56, amountText.length * 7);
        
        barGroup.append('rect')
            .attr('x', barWidth / 2 - blobWidth / 2)
            .attr('y', 30 + this.barHeight + 60)
            .attr('width', blobWidth)
            .attr('height', 20)
            .attr('rx', 10)
            .attr('fill', colors.blobBg)
            .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.08))')
            .style('opacity', 0)
            .transition()
            .delay(this.animationDuration)
            .duration(300)
            .style('opacity', 1);

        // Amount Text (re-enabled generic logic)
        // If the original code commented this out, I should check if I need it. 
        // The original check had `// Amount text` commented out but blob was there.
        // Wait, the original code had the blob RECT but commented out the TEXT inside it?
        // Let's check lines 267-279 in original. Yes commented out.
        // But the previous blob logic (lines 252-264) draws a rect. 
        // And then line 249 `const amountText = ...` was used for `blobWidth`.
        // So the rect is drawn based on text width, but text isn't drawn? That seems like a bug or incomplete feature in original.
        // I will uncomment/implement the text drawing effectively if it's meant to be there.
        // Actually looking at original, `barGroup.append('rect')` is lines 252-258.
        // The `amountText` variable is used to calculate width.
        // Visuals would be empty blob if text is missing.
        // I'll add the text back if `amountKey` is present, it makes sense.
        
        barGroup.append('text')
            .attr('x', barWidth / 2)
            .attr('y', 30 + this.barHeight + 71) // Adjusted Y centered in blob
            .attr('text-anchor', 'middle')
            .attr('fill', colors.textAmount)
            .attr('font-size', '11px')
            .attr('font-weight', '600')
            .attr('dominant-baseline', 'middle')
            .style('opacity', 0)
            .text(amountText)
            .transition()
            .delay(this.animationDuration)
            .duration(300)
            .style('opacity', 1);
      }

      // "per pay" label or generic secondary label
      if (this.showSecondaryLabel && this.secondaryLabelText) {
        barGroup.append('text')
            .attr('x', barWidth / 2)
            .attr('y', 30 + this.barHeight + 90) // Shifted down due to amount text
            .attr('text-anchor', 'middle')
            .attr('fill', colors.textSecondary)
            .attr('font-size', '9px')
            .style('opacity', 0)
            .text(this.secondaryLabelText)
            .transition()
            .delay(this.animationDuration)
            .duration(300)
            .style('opacity', 1);
      }

      // Rank badge at the bottom
      if (this.showRankBadges) {
        this.drawRankBadge(barGroup, barWidth, i, colors, true);
      }

      // Hover effect
      barGroup.style('cursor', 'pointer')
        .on('mouseenter', function() {
          select(this).select('rect:nth-child(3)')
            .transition()
            .duration(200)
            .attr('opacity', 0.85);
        })
        .on('mouseleave', function() {
          select(this).select('rect:nth-child(3)')
            .transition()
            .duration(200)
            .attr('opacity', 1);
        });
    });
  }

  // Helper for formatting values based on type
  private formatValue(value: number): string {
      if (this.valueFormat === 'percentage') return `${value}%`;
      if (this.valueFormat === 'currency') return this.formatCurrency(value);
      return `${value}`;
  }

  private drawRankBadge(
    group: Selection<SVGGElement, unknown, null, undefined>,
    barWidth: number,
    index: number,
    colors: ReturnType<typeof this.getThemeColors>,
    atBottom: boolean = false
  ) {
    const badgeSize = 20;
    const badgeX = barWidth / 2;
    const badgeY = atBottom ? (30 + this.barHeight + 15) : 10; // Adjusted for better positioning

    // Badge circle with gradient
    if (index === 0) {
      const gradId = 'badge-gold';
      const grad = group.append('defs').append('linearGradient')
        .attr('id', gradId)
        .attr('x1', '0%').attr('y1', '0%')
        .attr('x2', '100%').attr('y2', '100%');
      grad.append('stop').attr('offset', '0%').attr('stop-color', colors.rankGold[0]);
      grad.append('stop').attr('offset', '100%').attr('stop-color', colors.rankGold[1]);
      
      group.append('circle')
        .attr('cx', badgeX)
        .attr('cy', badgeY)
        .attr('r', badgeSize / 2)
        .attr('fill', `url(#${gradId})`);
    } else if (index === 1) {
      const gradId = 'badge-silver';
      const grad = group.append('defs').append('linearGradient')
        .attr('id', gradId)
        .attr('x1', '0%').attr('y1', '0%')
        .attr('x2', '100%').attr('y2', '100%');
      grad.append('stop').attr('offset', '0%').attr('stop-color', colors.rankSilver[0]);
      grad.append('stop').attr('offset', '100%').attr('stop-color', colors.rankSilver[1]);
      
      group.append('circle')
        .attr('cx', badgeX)
        .attr('cy', badgeY)
        .attr('r', badgeSize / 2)
        .attr('fill', `url(#${gradId})`);
    } else if (index === 2) {
      const gradId = 'badge-bronze';
      const grad = group.append('defs').append('linearGradient')
        .attr('id', gradId)
        .attr('x1', '0%').attr('y1', '0%')
        .attr('x2', '100%').attr('y2', '100%');
      grad.append('stop').attr('offset', '0%').attr('stop-color', colors.rankBronze[0]);
      grad.append('stop').attr('offset', '100%').attr('stop-color', colors.rankBronze[1]);
      
      group.append('circle')
        .attr('cx', badgeX)
        .attr('cy', badgeY)
        .attr('r', badgeSize / 2)
        .attr('fill', `url(#${gradId})`);
    } else {
      group.append('circle')
        .attr('cx', badgeX)
        .attr('cy', badgeY)
        .attr('r', badgeSize / 2)
        .attr('fill', colors.rankDefault);
    }

    // Badge number
    group.append('text')
      .attr('x', badgeX)
      .attr('y', badgeY + 1)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('fill', index > 2 ? colors.rankDefaultText : 'white')
      .attr('font-size', '10px')
      .attr('font-weight', 'bold')
      .text(index + 1);
  }

  private createGradient(
    svg: Selection<SVGSVGElement, unknown, null, undefined>,
    id: string,
    index: number,
    colors: ReturnType<typeof this.getThemeColors>
  ) {
    const defs = svg.append('defs');
    const gradient = defs.append('linearGradient')
      .attr('id', id)
      .attr('x1', '0%')
      .attr('y1', '100%')
      .attr('x2', '0%')
      .attr('y2', '0%');

    let gradColors: string[];
    if (index === 0) gradColors = colors.barGradient1;
    else if (index === 1) gradColors = colors.barGradient2;
    else if (index === 2) gradColors = colors.barGradient3;
    else gradColors = colors.barGradientDefault;

    gradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', gradColors[0]);
    gradient.append('stop')
      .attr('offset', '50%')
      .attr('stop-color', gradColors[1]);
    gradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', gradColors[2]);
  }

  private truncateText(text: string, maxWidth: number): string {
    const maxChars = Math.floor(maxWidth);
    return text.length > maxChars ? text.substring(0, maxChars - 3) + '...' : text;
  }

  private formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }

  onViewAll() {
    this.viewAll.emit();
  }
}
